import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Popover, Typography } from '@mui/material';

import { DoubleLayerFilterMenu } from './DoubleLayerFilterMenu';
import { popoverFilterMenuStyles as styles } from './popoverFilterMenu.styles';
import { SingleLayerFilterMenu } from './SingleLayerFilterMenu';

const POPOVER_FILTER_MENU_STRINGS = appStrings.modules.popoverFilterMenu;

export type PopoverFilterMenuItem<T = string> = Readonly<{
  label: string;
  value: string;
  category: T;
  icon?: AppIcons;
  isSelected?: boolean;
  onClick: () => void;

  rightIcon?: AppIcons;
  closeOnClick?: boolean;
}>;

export type FilterCategoryConfig<T = string> = Readonly<{
  label: string;
  items: PopoverFilterMenuItem<T>[];
}>;

type PopoverFilterMenuProps<T = string> = Readonly<{
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  categories: FilterCategoryConfig<T>[];
  clearFilters?: () => void;
  /** If true, shows all items in a single flat list without category navigation */
  singleLevel?: boolean;
  /** Optional title to display at the top of the menu */
  title?: string;
}>;

export const PopoverFilterMenu = <T extends string>({
  anchorEl,
  setAnchorEl,
  categories,
  clearFilters,
  singleLevel = false,
  title,
}: PopoverFilterMenuProps<T>) => {
  const [selectedCategory, setSelectedCategory] = useState<T | null>(null);

  const hasActiveFilters = categories.some((category) =>
    category.items.some((item) => item.isSelected),
  );

  const ClearAllButton = () => {
    if ((singleLevel || selectedCategory === null) && hasActiveFilters && clearFilters) {
      return (
        <Box id="filter-clear-all-button" onClick={clearFilters} sx={styles.clearAllButtonSx}>
          <Typography variant="text-sm-semibold" sx={styles.clearAllTextSx}>
            {POPOVER_FILTER_MENU_STRINGS.clearAll}
          </Typography>
        </Box>
      );
    }

    return null;
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        setAnchorEl(null);
        setSelectedCategory(null);
      }}
      PaperProps={{
        sx: styles.popoverContainerSx,
      }}
    >
      <Box sx={styles.popoverContentSx}>
        {title && (
          <Typography variant="text-sm-semibold" sx={styles.titleSx}>
            {title.length > 30 ? `${title.slice(0, 30)}...` : title}
          </Typography>
        )}

        {singleLevel && (
          <SingleLayerFilterMenu
            setAnchorEl={setAnchorEl}
            categories={categories}
            setSelectedCategory={(category) => setSelectedCategory(category as T)}
          />
        )}

        {!singleLevel && (
          <DoubleLayerFilterMenu
            setAnchorEl={setAnchorEl}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={(category) => setSelectedCategory(category as T)}
          />
        )}

        <ClearAllButton />
      </Box>
    </Popover>
  );
};
